import { ReactNode } from 'react';

import { AttachmentFragment } from '@spotify/confidence-graphql';

import { createModifierClickHandler } from '../../artifactUtils';
import { useArtifactDisplay } from '../../hooks/useArtifactDisplay';
import { ArtifactActionsMenu } from '../ArtifactActionsMenu';
import { ArtifactIcon } from '../ArtifactIcon';
import { ArtifactListItem } from '../ArtifactListItem';

interface DefaultArtifactListItemProps {
  artifact: AttachmentFragment;
  resolvedDisplayName?: string;
  icon?: ReactNode;
  href?: string;
  secondary?: ReactNode;
  disabled?: boolean;
  onSelect: () => void;
  onNavigateToResource?: () => void;
  onDelete?: () => void;
}

export const DefaultArtifactListItem = ({
  artifact,
  resolvedDisplayName,
  icon,
  href,
  secondary,
  disabled = false,
  onSelect,
  onNavigateToResource,
  onDelete,
}: DefaultArtifactListItemProps) => {
  const { Icon, typeLabel, displayName } = useArtifactDisplay(
    { uri: artifact.uri, attachment: artifact },
    resolvedDisplayName,
  );
  const dateLabel = new Date(artifact.createTime).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <ArtifactListItem.Root
      onClick={
        disabled ? undefined : createModifierClickHandler(href, onSelect)
      }
      ariaDisabled={disabled}
     data-track-source-anchor="90c8c880c8;default-artifact-list-item-root">
      <ArtifactListItem.Icon>
        <ArtifactIcon>{icon ?? <Icon size={16} />}</ArtifactIcon>
      </ArtifactListItem.Icon>
      <ArtifactListItem.Content
        primary={displayName}
        secondary={
          secondary ?? (
            <>
              {typeLabel} &middot; {dateLabel}
            </>
          )
        }
      />
      {!disabled && (
        <ArtifactListItem.Action>
          <ArtifactActionsMenu
            artifact={artifact}
            displayName={displayName}
            resourcePath={href}
            resolvedDisplayName={resolvedDisplayName}
            onNavigateToResource={onNavigateToResource}
            navigateLabel={
              onNavigateToResource ? `Go to ${typeLabel}` : undefined
            }
            onDelete={onDelete}
          />
        </ArtifactListItem.Action>
      )}
    </ArtifactListItem.Root>
  );
};
